import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import { Checkbox, FlexRow, IconButton, LocaleNumberField, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { grey, white } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { BaseCompositionMaterial } from "./BaseCompositionMaterial";
import { useBaseCompositionStore } from "./BaseCompositionStore";
import { CompositionPlacement } from "./CompositionPlacement";
import { MaterialOnBOM } from "./MaterialOnBOM";

interface BaseCompositionMaterialItemProps {
	currentCompositionMaterial: BaseCompositionMaterial;
	parent: CompositionPlacement | MaterialOnBOM;
}

const BaseCompositionMaterialItem: FunctionComponent<BaseCompositionMaterialItemProps> = observer(
	function BaseCompositionMaterialSingleItem(props) {
		const { currentCompositionMaterial, parent } = props;
		const {
			compositionMaterialItemsForDropdown,
			currentCompositionDropdownValue,
			currentCompositionTypeDropdownValue,
			currentUrl,
			getCompositionMaterialTypesForDropdown,
			getShowErrorCompositionMaterial,
			key,
			nestUnderContainsPlacement,
			percentageFixed,
			rawPercentage,
			restrictedPercentage,
			setCompositionMaterialPercentage,
			setCompositionMaterialTypeUrl,
			setNestUnderContainsPlacement,
			setUrlFromItem,
		} = currentCompositionMaterial;
		const {
			hasCompositionMaterialTypesForDropdown,
			onClickCompositionMaterialGoToDialog,
			percentageIsInvalid,
			removeBaseCompositionMaterial,
		} = parent;

		const loc = useLocale();
		const {
			allowZeroPercent,
			disableCompositionMaterialEdit,
			fromStyleBom,
			getSelectActions,
			hasSustainability,
			shouldInputAutoFocus,
		} = useBaseCompositionStore();
		return (
			<>
				<FlexRow
					alignItems="center"
					bgcolor={fromStyleBom ? grey[50] : white[50]}
					border={fromStyleBom ? 0 : 1}
					borderColor={grey[300]}
					borderRadius={1}
					data-csi-automation="CompositionMaterialBreakup"
					gap={2}
					paddingLeft={2}
					paddingRight={fromStyleBom ? 0 : 2}
					paddingY={fromStyleBom ? 2 : 3}
					width="100%"
				>
					<SingleSelect
						dataCsiAutomation="Select-Composition-Material"
						disableClearable
						disabled={disableCompositionMaterialEdit}
						fullWidth
						inputAutoFocus={shouldInputAutoFocus}
						onChange={setUrlFromItem}
						openOnFocus={shouldInputAutoFocus}
						options={compositionMaterialItemsForDropdown}
						placeHolderRes="CompositionNoSelectedItems"
						selectActions={compositionMaterialItemsForDropdown.length > 0 ? getSelectActions(onClickCompositionMaterialGoToDialog) : null}
						value={currentCompositionDropdownValue}
					/>
					{hasSustainability && (
						<SingleSelect
							dataCsiAutomation="Select-Composition-Type"
							disableClearable
							disabled={!hasCompositionMaterialTypesForDropdown(currentUrl) || disableCompositionMaterialEdit}
							fullWidth
							onChange={setCompositionMaterialTypeUrl}
							options={getCompositionMaterialTypesForDropdown(currentUrl)}
							placeHolderRes={hasCompositionMaterialTypesForDropdown(currentUrl) ? "CompositionTypeNoSelectedItems" : "CompositionTypeNoAvailableItems"}
							value={currentCompositionTypeDropdownValue}
						/>
					)}
					<LocaleNumberField
						InputProps={{
							endAdornment:
								<InputAdornment position="end">
									{loc.resource("PercentageSign")}
								</InputAdornment>,
						}}
						dataCsiAutomation="textfield-Composition-Material-Percentage"
						defaultValue={percentageFixed}
						disabled={disableCompositionMaterialEdit}
						error={getShowErrorCompositionMaterial(percentageIsInvalid)}
						onChange={setCompositionMaterialPercentage}
						placeholderRes="FormatDoublePercentage"
						required
						selectOnFocus
						sx={{ width: 100, minWidth: 100 }}
						value={restrictedPercentage}
					/>
					<IconButton
						disabled={disableCompositionMaterialEdit}
						onClick={() => removeBaseCompositionMaterial(key)}
						size="small"
					>
						<CloseIcon />
					</IconButton>
				</FlexRow>
				{allowZeroPercent && rawPercentage === 0 && (
					<FlexRow
						paddingBottom={2}
						paddingLeft={2}
					>
						<Checkbox
							checked={nestUnderContainsPlacement}
							labelProps={{ label: loc.resource("NestUnder"), sx: { fontWeight: 600 } }}
							onChange={setNestUnderContainsPlacement}
						/>
					</FlexRow>
				)}
			</>

		);
	},
);

export default BaseCompositionMaterialItem;